<template>
  <input
    class="number-input-field"
    type="text"
    :inputmode="displayCustomKeyboard ? 'none' : 'decimal'"
    v-model="internalValue"
    :pattern="patternRegex"
    :disabled="disabled"
    @focus="onFocus"
    @blur="onBlur"
    ref="input"
  />
</template>

<script>
import { KeyboardStoreNamespacedTypes } from '@/store/keyboard'
import { isOnIosDevice, isOnSamsungDevice } from '@/utils/device'

/**
 * The decimal symbol to use.
 * @type {string}
 */
const decimalSymbol = ','

/**
 * An alternative decimal symbol.
 * This symbol will be converted to the primary decimal symbol when entered.
 * @type {string}
 */
const alternativeDecimalSymbol = '.'

/**
 * A regex to clean up user input to match our validation regex patterns.
 * @type {RegExp}
 */
const cleanupRegex = new RegExp(
  `^[^\\d-]*(-?\\d+${decimalSymbol}?\\d*|-),?.*$`,
  'i'
)

/**
 * The pattern that should be matched when a complete answer is tested.
 * This pattern requires the following:
 *  - The string can start with a - sign or a digit
 *  - At least one digit should be present
 *  - After the first digit(s) a decimal symbol followed by even more digits are allowed
 *  - Nothing not described above is allowed
 * @type {string}
 */
const hardPatternRegex = `^-?\\d+([${decimalSymbol}][\\d]+)?`

/**
 * The pattern that should be matched when a partial answer is tested.
 * This pattern matches the same as the hard patter, but allows for 0 digits before or after the decimal symbol,
 * since that's a common case while typing an answer.
 * @type {string}
 */
const softPatternRegex = `^-?\\d*([${decimalSymbol}][\\d]*)?`

export default {
  name: 'NumberInputField',
  props: {
    /**
     * The value for the input field.
     */
    value: { type: String, default: '' },

    /**
     * Disables the input field.
     */
    disabled: { type: Boolean, default: false },

    /**
     * Forces the custom decimal keyboard to be displayed.
     * This property is intended for usage in Storybook only.
     */
    enforceCustomKeyboard: { type: Boolean, default: false },
  },
  data() {
    return {
      displayCustomKeyboard:
        this.enforceCustomKeyboard || isOnSamsungDevice() || isOnIosDevice(),

      /**
       * @type {string|null}
       */
      internalValue: this.value,

      /**
       * The regex to apply to the pattern attribute of the input field.
       * @type {string}
       */
      patternRegex: hardPatternRegex,
    }
  },
  beforeDestroy() {
    this.$store.dispatch(KeyboardStoreNamespacedTypes.actions.CLEAR_FOCUS, this)
  },
  computed: {
    /**
     * Returns true if a keypress in currently in progress on the software keyboard
     */
    keypressInProgress() {
      return this.$store.getters[
        KeyboardStoreNamespacedTypes.getters.IS_KEYPRESS_IN_PROGRESS
      ]()
    },
  },
  methods: {
    /**
     * Adds focus to the input field
     */
    focus() {
      this.$refs.input.focus()

      if (!this.disabled) {
        this.onFocus()
      }
    },

    /**
     * Removes focus from the input field
     */
    blur() {
      this.$refs.input.blur()
      this.onBlur()
    },

    /**
     * Relays the focus to the keyboard store and updates the regex pattern used for live validation.
     */
    onFocus() {
      if (this.displayCustomKeyboard) {
        this.$store.dispatch(
          KeyboardStoreNamespacedTypes.actions.SET_FOCUS,
          this
        )
      }

      this.setSoftRegexPattern()
    },

    /**
     * Removes the focus from the keyboard store and updates the regex pattern used for live validation.
     * Ignores blur events caused by pressing keyboard buttons.
     */
    onBlur() {
      if (!this.keypressInProgress) {
        this.$store.dispatch(
          KeyboardStoreNamespacedTypes.actions.CLEAR_FOCUS,
          this
        )
        this.setHardRegexPattern()
      }
    },

    /**
     * Returns the current value or an empty string if no current value exists.
     *
     * @returns {string}
     * @public
     */
    getValue() {
      return this.internalValue || ''
    },

    /**
     * Sets the current value.
     *
     * @param {string} newValue
     * @public
     */
    setValue(newValue) {
      this.internalValue = newValue
    },

    /**
     * Adds the given character to the internal value at the position of the cursor.
     * If a part of the value is selected in the input field, the selected part will be replaced by the given character.
     *
     * @param {string} character
     * @public
     */
    addCharacter(character) {
      const valueArray = this.internalValue.split('')
      const selectionStart = this.getSelectionStart()
      const selectionEnd = this.getSelectionEnd()

      // Remove the selected part of the value
      valueArray.splice(selectionStart, selectionEnd - selectionStart).join('')

      // Store both ends of the value as separate strings
      const start = valueArray.splice(0, selectionStart).join('')
      const end = valueArray.join('')

      // Glue it all together
      this.internalValue = `${start}${character}${end}`

      this.$nextTick(() => {
        // Reposition the cursor at the correct position
        const newPosition = selectionStart + 1

        this.setSelectionRange(newPosition, newPosition)
      })
    },

    /**
     * Removes the selected characters or the character to the left of the cursor if nothing is selected.
     *
     * @public
     */
    removeCharacter() {
      const selectionEnd = this.getSelectionEnd()

      let selectionStart = this.getSelectionStart()

      // Stop here if we're at the beginning of the input field and nothing is selected
      if (selectionStart === 0 && selectionEnd === 0) {
        return
      }

      const valueArray = this.internalValue.split('')

      // Calculate the selection range
      let selectionRange = selectionEnd - selectionStart

      // Select the character to the left of the cursor if nothing is selected
      if (selectionRange === 0) {
        selectionStart--
        selectionRange = 1
      }

      // Remove the selection from the value
      valueArray.splice(selectionStart, selectionRange)

      // Apply the value
      this.internalValue = valueArray.join('')

      this.$nextTick(() => {
        // Reposition the cursor at the correct position
        this.setSelectionRange(selectionStart, selectionStart)
      })
    },

    /**
     * Applies the soft regex to the pattern attribute of the input field.
     * This allows for browser validation of the input.
     */
    setSoftRegexPattern() {
      this.patternRegex = softPatternRegex
    },

    /**
     * Applies the hard regex to the pattern attribute of the input field.
     * This allows for browser validation of the input.
     */
    setHardRegexPattern() {
      this.patternRegex = hardPatternRegex
    },

    /**
     * Emits the current value as an input event.
     */
    emitValue() {
      this.$emit('input', this.internalValue)
    },

    /**
     * Returns a cleaned up version of the given value based on the validation regex.
     * @param {string} value
     * @returns {string}
     */
    extractCleanValue(value) {
      const matches = cleanupRegex.exec(value)

      return matches ? matches[1] : ''
    },

    /**
     * Replaces the alternative decimal symbol with the correct decimal symbol in the given string.
     * Ignores null values.
     *
     * @param {string|null} value
     * @returns {string|null}
     */
    streamlineDecimalSymbol(value) {
      if (value === null) {
        return null
      }

      return value.replace(alternativeDecimalSymbol, decimalSymbol)
    },

    /**
     * Returns true if the given value is valid according to our cleanup regex.
     *
     * @param {string|null} value
     * @returns {boolean}
     */
    isValueValid(value) {
      return value === this.extractCleanValue(value)
    },

    /**
     * Returns the start position of the selection in the input field.
     *
     * @returns {number}
     */
    getSelectionStart() {
      return this.$refs.input.selectionStart
    },

    /**
     * Returns the end position of the selection in the input field.
     *
     * @returns {number}
     */
    getSelectionEnd() {
      return this.$refs.input.selectionEnd
    },

    /**
     * Sets a new selection in the input field.
     *
     * @param {number} start
     * @param {number} end
     */
    setSelectionRange(start, end) {
      this.$refs.input.setSelectionRange(start, end)
    },

    /**
     * Ads and removes the invalid keystroke class on the input element as quickly as possible
     * to let the CSS transitions do their magic.
     */
    invalidKeystrokeBlink() {
      this.$refs.input.classList.add('invalid-keystroke')

      requestAnimationFrame(() => {
        this.$refs.input.classList.remove('invalid-keystroke')
      })
    },
  },
  watch: {
    /**
     * Updates the internal value to match the value property.
     * @param {string|null} value
     */
    value(value) {
      this.internalValue = value
    },

    /**
     * Validates and cleans up the user input and makes sure it matches our input requirements.
     * The final value is emitted as an input event.
     * @param {string} newInternalValue
     * @param {string|null} oldInternalValue
     */
    internalValue(newInternalValue, oldInternalValue = '') {
      // Store the current selection start, so that we can position the cursor at the same point later
      const selectionStart = this.getSelectionStart()

      // Streamline decimal symbols in both the new and old value
      const internalValue = this.streamlineDecimalSymbol(newInternalValue)
      const decimalSymbolChanged = internalValue !== newInternalValue

      oldInternalValue = this.streamlineDecimalSymbol(oldInternalValue)

      // Start error handling if the internal value isn't valid
      if (!this.isValueValid(internalValue)) {
        if (this.isValueValid(oldInternalValue)) {
          // Revert to the old internal value if it was a valid value
          this.internalValue = oldInternalValue
        } else {
          // Clear the input field if the old value wasn't valid either
          this.internalValue = ''
        }

        // Blink invalid keystroke style on the input field
        this.invalidKeystrokeBlink()

        this.$nextTick(() => {
          let cursorMovement = 0

          // If using the custom keyboard the following will introduce race conditions placing the cursor in the wrong position.
          // Not moving the cursor results in the desired behaviour.
          if (!this.displayCustomKeyboard) {
            // Replace the cursor in the input field at its previous position
            cursorMovement = internalValue.length - oldInternalValue.length
          }

          this.setSelectionRange(
            selectionStart - cursorMovement,
            selectionStart - cursorMovement
          )
        })
      }
      // Check if the decimal symbol was changed
      else if (decimalSymbolChanged) {
        // Apply the internal value with the updated decimal point
        this.internalValue = internalValue

        this.$nextTick(() => {
          // Replace the cursor in the input field at its correct position
          // (The cursor was moved to the end of the field because we just changed the internal value)
          this.setSelectionRange(selectionStart, selectionStart)
        })
      }

      this.emitValue()
    },
  },
}
</script>

<style lang="scss" scoped>
.number-input-field {
  min-height: 1.466em;
  transition: all 0.5s ease-out;

  &.invalid-keystroke {
    transition-duration: 0s;
    background: $errorBackground;
  }
}
</style>
